import type {
  CellClickedEventArgs,
  GridSelection,
  Item,
} from "@glideapps/glide-data-grid";
import { useCallback, useEffect, useRef, useState } from "react";
import type { CellCommentCardTarget } from "@/payroll/ai-script/cell-comments/cell-comment-card";
import type { IAiSpreadsheetRegion } from "@/payroll/ai-script/types";
import {
  matchesMultiSelectModifier,
  toggleCellInSelection,
} from "./cell-selection";
import type { ColumnHeaderMenuTarget } from "./column-header-menu";
import type { RowInsertMenuTarget } from "./row-insert-menu";
import type { SpreadsheetColumnMenuTarget } from "./spreadsheet-column-menu";
import type { SpreadsheetRegionDraft } from "./spreadsheet-region";
import type { SpreadsheetRegionViewStates } from "./spreadsheet-region-view";
import { emptySelection } from "./utils";

interface UseViewerUiStateParams {
  controlledActiveSheetId: string | null | undefined;
  onActiveSheetChange: ((sheetId: string) => void) | undefined;
  onMaximizedChange: ((maximized: boolean) => void) | undefined;
  isMobile: boolean;
}

export const useViewerUiState = ({
  controlledActiveSheetId,
  onActiveSheetChange,
  onMaximizedChange,
  isMobile,
}: UseViewerUiStateParams) => {
  const [uncontrolledActiveSheetId, setUncontrolledActiveSheetId] = useState<
    string | null
  >(null);
  const activeSheetId =
    controlledActiveSheetId === undefined
      ? uncontrolledActiveSheetId
      : controlledActiveSheetId;
  const selectSheet = useCallback(
    (sheetId: string) => {
      if (controlledActiveSheetId === undefined) {
        setUncontrolledActiveSheetId(sheetId);
      }
      onActiveSheetChange?.(sheetId);
    },
    [controlledActiveSheetId, onActiveSheetChange],
  );
  const [gridSelection, setGridSelection] =
    useState<GridSelection>(emptySelection);
  const selectionBeforePointerDownRef = useRef<GridSelection>(emptySelection);
  const [isMaximized, setIsMaximized] = useState(false);
  const [isSheetSwitcherOpen, setIsSheetSwitcherOpen] = useState(false);
  const [pendingCell, setPendingCell] = useState<{
    sheetId: string;
    row: number;
    col: number;
  } | null>(null);
  const [rowMenu, setRowMenu] = useState<RowInsertMenuTarget | null>(null);
  const [commentCard, setCommentCard] = useState<CellCommentCardTarget | null>(
    null,
  );
  const pendingCommentOpenRef = useRef<{
    sheetId: string;
    row: number;
    col: number;
  } | null>(null);
  const [commentCardTick, setCommentCardTick] = useState(0);
  const [columnHeaderMenu, setColumnHeaderMenu] =
    useState<ColumnHeaderMenuTarget | null>(null);
  const [spreadsheetColumnMenu, setSpreadsheetColumnMenu] =
    useState<SpreadsheetColumnMenuTarget | null>(null);
  const [regionPreview, setRegionPreview] =
    useState<SpreadsheetRegionDraft | null>(null);
  const [regionDialogTarget, setRegionDialogTarget] = useState<{
    sheetId: string;
    selection: GridSelection;
    existingRegion?: IAiSpreadsheetRegion;
  } | null>(null);
  const [regionViewStatesBySheet, setRegionViewStatesBySheet] = useState<
    Record<string, SpreadsheetRegionViewStates>
  >({});
  // Glide's onCellContextMenu has no pointer coords; track the last contextmenu event.
  const lastContextPointerRef = useRef<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });

  const handleGridSelectionChange = (newSelection: GridSelection) => {
    setGridSelection((currentSelection) => {
      const nextRow = newSelection.rows.first();
      const nextColumn = newSelection.columns.first();
      const isReselectedRow =
        nextRow !== undefined && nextRow === currentSelection.rows.first();
      const isReselectedColumn =
        nextColumn !== undefined &&
        nextColumn === currentSelection.columns.first();

      return isReselectedRow || isReselectedColumn
        ? emptySelection
        : newSelection;
    });
  };

  const handleMultiSelectCellClick = (
    cell: Item,
    event: CellClickedEventArgs,
  ) => {
    if (isMobile || !matchesMultiSelectModifier(event)) return false;

    event.preventDefault();
    setGridSelection(
      toggleCellInSelection(selectionBeforePointerDownRef.current, cell),
    );
    return true;
  };

  useEffect(() => {
    if (!isMaximized) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      setIsMaximized(false);
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isMaximized]);

  useEffect(() => {
    onMaximizedChange?.(isMaximized);
  }, [isMaximized, onMaximizedChange]);

  return {
    activeSheetId,
    selectSheet,
    gridSelection,
    setGridSelection,
    selectionBeforePointerDownRef,
    isMaximized,
    setIsMaximized,
    isSheetSwitcherOpen,
    setIsSheetSwitcherOpen,
    pendingCell,
    setPendingCell,
    rowMenu,
    setRowMenu,
    commentCard,
    setCommentCard,
    pendingCommentOpenRef,
    commentCardTick,
    setCommentCardTick,
    columnHeaderMenu,
    setColumnHeaderMenu,
    spreadsheetColumnMenu,
    setSpreadsheetColumnMenu,
    regionPreview,
    setRegionPreview,
    regionDialogTarget,
    setRegionDialogTarget,
    regionViewStatesBySheet,
    setRegionViewStatesBySheet,
    lastContextPointerRef,
    handleGridSelectionChange,
    handleMultiSelectCellClick,
  };
};

export type ViewerUiState = ReturnType<typeof useViewerUiState>;
